import React, { FC, SetStateAction, useEffect, useState } from 'react';
import { AntdWrapper } from '../../../clients/shared/AntdWrapper';
import { Layout, Flex } from 'antd';
import { LeftNav } from './LeftNav';
import { RightNav } from './RightNav';
import styles from './index.module.css';
import cx from 'classnames';
import useMediaQuery from '../../../../clients/sidebar/views/CreateTask/create_task/useMediaQuery';
import { Props } from './types';
import { MobileNav } from './MobileNav';
import { Sun, Moon } from 'lucide-react';
import {
  OrganizationProvider,
  useOrganizationState,
  OrganizationProject
} from '../../../../clients/providers/Organization';
import track from '../../utils/analytics';

type OrganizationProjects = OrganizationProject[];

const Navbar: FC<Props> = Props => {
  const { user, isActiveOrganization, endpoints } = Props;
  const { isImpersonating, organizations } = user;
  const body = document.body;
  const isMobileView = useMediaQuery('(max-width: 992px)');
  const container = document.getElementById('navbar_container');
  const [isMobileMenuOpen, setMobileMenu] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIntercomOpen, setIsIntercomOpen] = useState<boolean>(false);
  const [isDarkMode, setDarkMode] = useState(
    () => localStorage.getItem('theme') || 'light'
  );
  const [darkThemeIcon, setDarkThemeIcon] = useState<React.JSX.Element>(
    <Moon size={20} />
  );
  const [darkModeString, setDarkModeString] = useState<
    string | SetStateAction<SetStateAction<string>>
  >('Light mode');
  const { organization } = useOrganizationState();

  const toggleMobileMenu = () => {
    if (!isMobileView) return;
    setMobileMenu(!isMobileMenuOpen);

    //prevent user from scrolling when menu is open
    const reactContainer = document.getElementById('ReactApp');
    if (!isMobileMenuOpen && reactContainer) {
      reactContainer.style.overflow = 'hidden';
    } else if (isMobileMenuOpen && reactContainer) {
      reactContainer.style.overflow = 'unset';
    }
  };

  const initialFilteredProjects: OrganizationProjects[] = [[], []];
  const filteredProjects = organization?.projects.reduce((acc, project) => {
    acc[project.showOnLive !== false ? 0 : 1].push(project);
    return acc;
  }, initialFilteredProjects);

  const [enabledProjects, disabledProjects] =
    filteredProjects || initialFilteredProjects;

  const latestProjects = enabledProjects.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

  const multipleOrganizations = organizations?.length > 1;

  const trackDarkMode = mode => {
    track(`Dark mode ${mode === 'light' ? 'disabled' : 'enabled'}`, {
      organization: organization?.id,
      project: organization?.project?.id,
      user: user
    });
  };

  const toggleDarkTheme = () => {
    const newTheme = isDarkMode === 'light' ? 'dark-theme' : 'light';
    localStorage.setItem('theme', newTheme);
    setDarkMode(newTheme);
    body.classList.toggle('dark-theme');

    trackDarkMode(newTheme);
  };

  useEffect(() => {
    setDarkThemeIcon(
      isDarkMode === 'dark-theme' ? <Sun size={20} /> : <Moon size={20} />
    );
    setDarkModeString(
      isDarkMode === 'dark-theme' ? (
        'Light mode'
      ) : (
        <>
          Dark mode <span className={styles.betaMode}>New</span>
        </>
      )
    );
  }, [isDarkMode]);

  useEffect(() => {
    window.Intercom?.('onShow', () => setIsIntercomOpen(true));
    window.Intercom?.('onHide', () => setIsIntercomOpen(false));
  }, [window.Intercom]);

  return (
    <Layout className="layout">
      <div
        className={cx(styles.intercomMask, {
          [styles.hidden]: !isIntercomOpen
        })}
        onClick={() => window.Intercom?.('hide')}
      />
      <div className={styles.navWrapper}>
        <div
          className={cx(styles.navOuter, {
            [styles.navHidden]: !isMobileMenuOpen
          })}
        >
          <div
            className={cx(styles.navbarWrapper, {
              [styles.impersonating]: isImpersonating,
              [styles.navHidden]: isMobileMenuOpen && isMobileView
            })}
          >
            <Flex justify="space-between" gap="middle" align="center">
              <LeftNav
                endpoints={endpoints}
                user={user}
                isMobileView={isMobileView}
                container={container}
                latestProjects={latestProjects}
                disabledProjects={disabledProjects}
                multipleOrganizations={multipleOrganizations}
                toggleMobileMenu={toggleMobileMenu}
                isMobileMenuOpen={isMobileMenuOpen}
                organizations={organizations}
              />

              <RightNav
                isActiveOrganization={isActiveOrganization}
                endpoints={endpoints}
                user={user}
                isMobileView={isMobileView}
                container={container}
                setIsModalOpen={setIsModalOpen}
                isModalOpen={isModalOpen}
                toggleMobileMenu={toggleMobileMenu}
                toggleDarkMode={toggleDarkTheme}
                darkModeString={darkModeString}
                darkThemeIcon={darkThemeIcon}
              />
            </Flex>
          </div>
        </div>
        {isMobileView && (
          <MobileNav
            isMobileMenuOpen={isMobileMenuOpen}
            container={container}
            isActiveOrganization={isActiveOrganization}
            endpoints={endpoints}
            user={user}
            isMobileView
            toggleMobileMenu={toggleMobileMenu}
            latestProjects={latestProjects}
            multipleOrganizations={multipleOrganizations}
            setIsModalOpen={setIsModalOpen}
            toggleDarkMode={toggleDarkTheme}
            darkModeString={darkModeString}
          />
        )}
      </div>
    </Layout>
  );
};

export default (props: Props) => (
  <AntdWrapper>
    <OrganizationProvider
      apiDomain={props.endpoints.bugherdUrl}
      id={props.user.organization?.id}
    >
      <Navbar {...props} />
    </OrganizationProvider>
  </AntdWrapper>
);
